import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, Copy, Loader2, Share2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const C = { cream: "#F3EDDD", dim: "rgba(243,237,221,0.6)", gold: "#D7BB7A", mist: "rgba(215,187,122,0.22)", ink: "#070D0B" };
const SANS = '"Manrope", sans-serif';
const SERIF = '"Cormorant Garamond", serif';
export const shareUrl = (token) => `${window.location.origin}/dream/shared/${token}`;

const Btn = ({ children, solid, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p}
    className="rounded-full px-5 py-2.5 uppercase inline-flex items-center justify-center gap-2 transition-opacity duration-200 hover:opacity-85 disabled:opacity-40"
    style={{ border: `1px solid ${solid ? C.gold : C.mist}`, background: solid ? C.gold : "transparent", color: solid ? C.ink : C.cream, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.24em" }}>
    {children}
  </button>
);

const ShareSheet = ({ dream, onClose }) => {
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.post(`${API}/api/atelier/dreams/${dream.id}/share`, {}, auth())
      .then((r) => setToken(r.data.token)).catch(() => setError("We couldn't create the link just now. Please try again."));
  }, [dream.id]);
  const url = token ? shareUrl(token) : "";
  const text = `I'd love you to experience my dream, "${dream.title}". ${url}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); } catch { window.prompt("Copy this link", url); } };
  const native = () => navigator.share?.({ title: dream.title, text, url }).catch(() => {});
  const stop = async () => {
    if (!window.confirm("Stop sharing? The link will stop working for everyone you've sent it to. You can share it again later with the same link.")) return;
    try { await axios.delete(`${API}/api/atelier/dreams/${dream.id}/share`, auth()); onClose(); } catch { setError("We couldn't stop sharing just now. Please try again."); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(3,6,10,0.8)", backdropFilter: "blur(12px)" }} data-testid="share-dream-sheet">
      <div className="w-full max-w-md rounded-3xl p-6" style={{ background: "#0E1714", border: `1px solid ${C.mist}` }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.34em]" style={{ color: C.gold, fontFamily: SANS }}>Share this dream</p>
            <h3 className="mt-2 text-2xl italic" style={{ fontFamily: SERIF, color: C.cream }}>{dream.title}</h3>
            <p className="mt-2 text-sm" style={{ color: C.dim, fontFamily: SANS }}>Anyone with the link can listen to your installations and play the immersion. They can't change or download anything.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="share-dream-close" className="w-9 h-9 shrink-0 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${C.mist}`, color: C.cream }}><X size={15} /></button>
        </div>
        {error && <p className="mt-4 text-sm" style={{ color: "#E07A6A" }} data-testid="share-dream-error">{error}</p>}
        {!token && !error && <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={18} color={C.gold} /></div>}
        {token && (
          <>
            <div className="mt-5 rounded-xl px-4 py-3 text-xs break-all" style={{ background: "rgba(215,187,122,0.06)", border: `1px solid ${C.mist}`, color: C.cream, fontFamily: SANS }} data-testid="share-dream-url">{url}</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Btn solid onClick={copy} testId="share-dream-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy link"}</Btn>
              <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="share-dream-whatsapp"
                className="rounded-full px-5 py-2.5 uppercase inline-flex items-center justify-center gap-2" style={{ border: `1px solid ${C.mist}`, color: C.cream, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.24em" }}>WhatsApp</a>
            </div>
            {navigator.share && <div className="mt-2"><Btn onClick={native} testId="share-dream-more"><Share2 size={13} /> More ways to send</Btn></div>}
            <button type="button" onClick={stop} data-testid="share-dream-stop" className="mt-5 text-xs underline underline-offset-4" style={{ color: C.dim, fontFamily: SANS }}>Stop sharing (the link stops working)</button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export const ShareDreamButton = ({ dream }) => {
  const [open, setOpen] = useState(false);
  if (!dream || dream.status === "curating") return null;
  return (
    <div className="mt-5 flex justify-center">
      <Btn onClick={() => setOpen(true)} testId="share-dream-button"><Share2 size={13} /> Share this dream</Btn>
      {open && <ShareSheet dream={dream} onClose={() => setOpen(false)} />}
    </div>
  );
};

// Dreams other people shared and this user saved.
export const SharedWithMe = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/api/atelier/shared-with-me`, auth()).then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="mb-10" data-testid="library-shared-dreams">
      <p className="mb-3 text-[0.6rem] uppercase tracking-[0.32em]" style={{ color: C.gold, fontFamily: SANS }}>Dreams shared with you · {items.length}</p>
      <ul className="space-y-2">
        {items.map((d) => (
          <li key={d.token}>
            <Link to={`/dream/shared/${d.token}`} data-testid={`library-shared-dream-${d.token}`} className="flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors duration-150 hover:bg-white/5" style={{ border: `1px solid ${C.mist}` }}>
              {d.cover_url ? <img src={`${API}${d.cover_url}`} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <span className="w-12 h-12 rounded-xl" style={{ background: C.mist }} />}
              <span className="min-w-0">
                <span className="block truncate text-lg" style={{ fontFamily: SERIF, color: C.cream }}>{d.title}</span>
                <span className="block text-[0.62rem] uppercase tracking-[0.2em]" style={{ color: C.dim, fontFamily: SANS }}>From {d.shared_by}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
